import { useState, useEffect } from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, subWeeks, addMonths, subMonths, isSameDay, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const ClassCalendar = ({ classes = [], view = 'week' }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState(view);

  const navigatePrev = () => {
    if (calendarView === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(subMonths(currentDate, 1));
    }
  };

  const navigateNext = () => {
    if (calendarView === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getWeekDays = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(start, i));
    }
    return days;
  };

  const getMonthDays = () => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const startWeek = startOfWeek(start, { weekStartsOn: 1 });
    const endWeek = endOfWeek(end, { weekStartsOn: 1 });
    
    const days = [];
    let day = startWeek;
    while (day <= endWeek) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  };

  const getClassesForDay = (day) => {
    return classes.filter(cls => {
      const classDate = typeof cls.start === 'string' ? parseISO(cls.start) : cls.start;
      return isSameDay(classDate, day);
    });
  };

  const days = calendarView === 'week' ? getWeekDays() : getMonthDays();
  const hours = Array.from({ length: 14 }, (_, i) => i + 6); // 6 AM to 8 PM

  return (
    <div className="bg-background-paper border border-white/10 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={navigatePrev} data-testid="calendar-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday} data-testid="calendar-today">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={navigateNext} data-testid="calendar-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <h2 className="font-bold text-xl ml-4">
            {calendarView === 'week' 
              ? `${format(getWeekDays()[0], 'MMM d')} - ${format(getWeekDays()[6], 'MMM d, yyyy')}`
              : format(currentDate, 'MMMM yyyy')
            }
          </h2>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={calendarView === 'week' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setCalendarView('week')}
            data-testid="view-week-btn"
          >
            Week
          </Button>
          <Button 
            variant={calendarView === 'month' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setCalendarView('month')}
            data-testid="view-month-btn"
          >
            Month
          </Button>
        </div>
      </div>

      {/* Week View */}
      {calendarView === 'week' && (
        <div className="overflow-x-auto">
          {/* Day Headers */}
          <div className="grid grid-cols-8 border-b border-white/10">
            <div className="p-2 text-center text-xs text-neutral-500 border-r border-white/10">Time</div>
            {getWeekDays().map((day) => (
              <div 
                key={day.toISOString()} 
                className={`p-2 text-center border-r border-white/10 ${
                  isSameDay(day, new Date()) ? 'bg-primary/10' : ''
                }`}
              >
                <div className="text-xs text-neutral-500">{format(day, 'EEE')}</div>
                <div className={`font-bold ${isSameDay(day, new Date()) ? 'text-primary' : ''}`}>
                  {format(day, 'd')}
                </div>
              </div>
            ))}
          </div>

          {/* Time Grid */}
          <div className="relative" style={{ height: '600px', overflowY: 'auto' }}>
            {hours.map(hour => (
              <div key={hour} className="grid grid-cols-8 border-b border-white/5" style={{ height: '60px' }}>
                <div className="p-1 text-xs text-neutral-500 border-r border-white/10 flex items-start justify-center">
                  {hour}:00
                </div>
                {getWeekDays().map((day) => {
                  const dayClasses = getClassesForDay(day).filter(cls => {
                    const classDate = typeof cls.start === 'string' ? parseISO(cls.start) : cls.start;
                    return classDate.getHours() === hour;
                  });
                  
                  return (
                    <div key={day.toISOString()} className="border-r border-white/10 relative p-0.5">
                      {dayClasses.map((cls, clsIndex) => (
                        <Link
                          key={cls.class_id}
                          to={`/classes/${cls.class_id}`}
                          className="absolute inset-x-0.5 bg-primary/20 border-l-2 border-primary rounded-sm p-1 hover:bg-primary/30 transition-colors overflow-hidden"
                          style={{
                            top: `${clsIndex * 2}px`,
                            height: `${Math.min(cls.duration, 60) - 4}px`,
                            zIndex: clsIndex + 1
                          }}
                          data-testid={`calendar-event-${cls.class_id}`}
                        >
                          <p className="text-xs font-bold truncate">{cls.title}</p>
                          <p className="text-xs text-neutral-400 truncate">{cls.instructor}</p>
                        </Link>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Month View */}
      {calendarView === 'month' && (
        <div>
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-white/10">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} className="p-2 text-center text-xs text-neutral-500 font-bold">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {getMonthDays().map((day, index) => {
              const dayClasses = getClassesForDay(day);
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isToday = isSameDay(day, new Date());

              return (
                <div 
                  key={day.toISOString()} 
                  className={`min-h-[100px] p-1 border-b border-r border-white/5 ${
                    !isCurrentMonth ? 'bg-background/50 opacity-50' : ''
                  } ${isToday ? 'bg-primary/10' : ''}`}
                >
                  <div className={`text-sm mb-1 ${isToday ? 'text-primary font-bold' : 'text-neutral-400'}`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5">
                    {dayClasses.slice(0, 3).map(cls => (
                      <Link
                        key={cls.class_id}
                        to={`/classes/${cls.class_id}`}
                        className="block bg-primary/20 rounded-sm px-1 py-0.5 text-xs truncate hover:bg-primary/30 transition-colors"
                        data-testid={`calendar-month-event-${cls.class_id}`}
                      >
                        <span className="text-neutral-400">{format(parseISO(cls.start), 'HH:mm')}</span> {cls.title}
                      </Link>
                    ))}
                    {dayClasses.length > 3 && (
                      <p className="text-xs text-neutral-500 pl-1">+{dayClasses.length - 3} more</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassCalendar;
